import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  Eye, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  FileText,
  Clock,
  Filter,
  Search,
  ArrowDownAZ,
  ArrowUpAZ
} from 'lucide-react';
import { projectAPI, timesheetAPI } from '../services/api';
import { format } from 'date-fns';
import toast from 'react-hot-toast';

const ManagerTimesheets = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
  const [dateSortOrder, setDateSortOrder] = useState('newest'); // 'newest' | 'oldest'
  const [searchTerm, setSearchTerm] = useState('');
  const [timesheetIdSearch, setTimesheetIdSearch] = useState('');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  // Update status filter when URL query param changes (only on mount or when URL changes externally)
  useEffect(() => {
    const statusFromUrl = searchParams.get('status');
    if (statusFromUrl && statusFromUrl !== statusFilter) {
      setStatusFilter(statusFromUrl);
    }
  }, [searchParams]);

  // Update URL when status filter changes locally (but avoid if it matches URL param)
  useEffect(() => {
    const currentStatusInUrl = searchParams.get('status');
    if (statusFilter && currentStatusInUrl !== statusFilter) {
      setSearchParams({ status: statusFilter });
    } else if (!statusFilter && currentStatusInUrl) {
      setSearchParams({});
    }
  }, [statusFilter]);

  const { data: timesheets, isLoading, error, refetch } = useQuery(
    ['manager-timesheets', statusFilter],
    () => {
      console.log('ManagerTimesheets - User:', user);
      console.log('ManagerTimesheets - User role:', user?.role);
      console.log('ManagerTimesheets - Making API call...');
      // Backend doesn't filter by status, so fetch all and filter on frontend
      return projectAPI.getAllManagerTimesheets({});
    },
    {
      select: (response) => {
        console.log('ManagerTimesheets - API Response:', response);
        console.log('ManagerTimesheets - Response data:', response.data);
        console.log('ManagerTimesheets - Timesheets:', response.data?.timesheets);
        // The backend returns the data directly, not wrapped in a data property
        return response.data?.timesheets || [];
      },
      onError: (error) => {
        console.error('ManagerTimesheets - API Error:', error);
        console.error('ManagerTimesheets - Error details:', {
          message: error.message,
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          url: error.config?.url
        });
      },
      enabled: !!user // Only run the query if user is authenticated
    }
  );

  const approveMutation = useMutation(
    (timesheetId) => timesheetAPI.approveManagerTimesheet(timesheetId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('manager-timesheets');
        queryClient.invalidateQueries('manager-pending-timesheets');
        toast.success('Timesheet approved successfully');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to approve timesheet');
      },
    }
  );

  const rejectMutation = useMutation(
    ({ timesheetId, rejectionReason }) => timesheetAPI.rejectManagerTimesheet(timesheetId, rejectionReason),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('manager-timesheets');
        queryClient.invalidateQueries('manager-pending-timesheets');
        toast.success('Timesheet rejected successfully');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to reject timesheet');
      },
    }
  );

  const [rejectModal, setRejectModal] = useState({ isOpen: false, timesheetId: null });
  const [rejectionReason, setRejectionReason] = useState('');

  const handleReject = () => {
    if (!rejectionReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    
    rejectMutation.mutate({
      timesheetId: rejectModal.timesheetId,
      rejectionReason: rejectionReason.trim()
    });
    
    setRejectModal({ isOpen: false, timesheetId: null });
    setRejectionReason('');
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'APPROVED':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'REJECTED':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'PENDING':
        return <Clock className="w-5 h-5 text-yellow-500" />;
      case 'SUBMITTED':
        return <AlertCircle className="w-5 h-5 text-blue-500" />;
      case 'REUPDATED':
        return <AlertCircle className="w-5 h-5 text-orange-500" />;
      default:
        return <AlertCircle className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'APPROVED':
        return 'bg-green-100 text-green-800';
      case 'REJECTED':
        return 'bg-red-100 text-red-800';
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800';
      case 'SUBMITTED':
        return 'bg-blue-100 text-blue-800';
      case 'REUPDATED':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredTimesheets = timesheets?.filter(timesheet => {
    // Status filter - exact match for all statuses
    let matchesStatus = true;
    if (statusFilter) {
      matchesStatus = timesheet.status === statusFilter;
    }
    
    
    if (!matchesStatus) {
      return false;
    }
    
    // Check if searching by timesheet ID
    const hasTimesheetIdSearch = timesheetIdSearch && timesheetIdSearch.trim() !== '';
    
    // If searching by ID, check ID match first
    if (hasTimesheetIdSearch) {
      const searchId = String(timesheetIdSearch).trim();
      const timesheetId = timesheet.id ? String(timesheet.id) : '';
      return timesheetId.includes(searchId);
    } else {
      // Not searching by ID, check employee search
      const hasEmployeeSearch = searchTerm && searchTerm.trim() !== '';
      if (hasEmployeeSearch) {
        const searchLower = searchTerm.toLowerCase();
        const matchesEmployee = 
          timesheet.employee?.firstName?.toLowerCase().includes(searchLower) ||
          timesheet.employee?.lastName?.toLowerCase().includes(searchLower) ||
          timesheet.employee?.email?.toLowerCase().includes(searchLower);
        return matchesEmployee;
      }
      // No search criteria, show all (already filtered by status)
      return true;
    }
  }) || [];

  const toComparableDate = (timesheet) => {
    const dateValue = timesheet.submittedAt ||
      timesheet.updatedAt ||
      timesheet.createdAt ||
      timesheet.endDate ||
      timesheet.startDate;
    return dateValue ? new Date(dateValue) : null;
  };

  const sortedTimesheets = [...filteredTimesheets].sort((a, b) => {
    const dateA = toComparableDate(a);
    const dateB = toComparableDate(b);

    if (dateA && dateB) {
      const diff = dateSortOrder === 'newest' ? dateB - dateA : dateA - dateB;
      return diff;
    }
    if (dateA) return dateSortOrder === 'newest' ? -1 : 1;
    if (dateB) return dateSortOrder === 'newest' ? 1 : -1;
    return dateSortOrder === 'newest' ? (b.id || 0) - (a.id || 0) : (a.id || 0) - (b.id || 0);
  });

  // Check if user is authenticated and is a manager
  if (!user) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Authentication Required</h3>
        <p className="text-gray-600 mb-4">Please log in to access timesheet management.</p>
        <button
          onClick={() => navigate('/login')}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Go to Login
        </button>
      </div>
    );
  }

  if (user.role !== 'MANAGER') {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Access Denied</h3>
        <p className="text-gray-600 mb-4">You need manager privileges to access this page.</p>
        <p className="text-sm text-gray-500 mb-4">Current role: {user.role}</p>
        <button
          onClick={() => navigate('/')}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Go to Dashboard
        </button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    console.error('ManagerTimesheets - Full error object:', error);
    console.error('ManagerTimesheets - Error response:', error.response);
    console.error('ManagerTimesheets - Error message:', error.message);
    
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Timesheets</h3>
        <p className="text-gray-600 mb-4">
          {error.response?.data?.message || error.message || 'Failed to load timesheets'}
        </p>
        <div className="text-sm text-gray-500 mb-4">
          <p>Status: {error.response?.status}</p>
          <p>URL: {error.config?.url}</p>
        </div>
        <button
          onClick={() => refetch()}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Timesheet Management</h1>
          <p className="text-gray-600">Review and manage employee timesheets</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search by employee name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search by timesheet ID..."
                  value={timesheetIdSearch}
                  onChange={(e) => setTimesheetIdSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Statuses</option>
                <option value="SUBMITTED">Submitted</option>
                <option value="PENDING">Pending</option>
                <option value="REUPDATED">Re-updated</option>
                <option value="REJECTED">Rejected</option>
                <option value="APPROVED">Approved</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              {dateSortOrder === 'newest' ? (
                <ArrowDownAZ className="w-4 h-4 text-gray-400" />
              ) : (
                <ArrowUpAZ className="w-4 h-4 text-gray-400" />
              )}
              <select
                value={dateSortOrder}
                onChange={(e) => setDateSortOrder(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                title="Arrange by date"
              >
                <option value="newest">Newest first</option>
                <option value="oldest">Oldest first</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Timesheets List */}
      <div className="bg-white rounded-lg shadow-sm border">
        {filteredTimesheets.length === 0 ? (
          <div className="p-8 text-center">
            <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No timesheets found</h3>
            <p className="text-gray-600">
              {searchTerm || statusFilter 
                ? 'No timesheets match your current filters.' 
                : 'No timesheets have been submitted yet.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Employee
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Period
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Hours
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Submitted
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sortedTimesheets.map((timesheet) => (
                  <motion.tr
                    key={timesheet.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={(e) => {
                      // Don't navigate if clicking on action buttons
                      if (e.target.closest('button') || e.target.closest('svg')) {
                        return;
                      }
                      navigate(`/manager/timesheets/${timesheet.id}`);
                    }}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                            <span className="text-sm font-medium text-blue-600">
                              {timesheet.employee?.firstName?.charAt(0)}{timesheet.employee?.lastName?.charAt(0)}
                            </span>
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="flex items-center space-x-2">
                            <div 
                              onClick={(e) => {
                                e.stopPropagation();
                                const userId = timesheet.employee?.id;
                                if (userId) {
                                  navigate(`/manager/users/${userId}/timesheets`);
                                }
                              }}
                              className="text-sm font-medium text-blue-600 cursor-pointer hover:text-blue-700 hover:underline transition-colors"
                            >
                              {timesheet.employee?.firstName} {timesheet.employee?.lastName}
                              <span className="ml-1 text-gray-400">→</span>
                            </div>
                            <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-700 rounded border border-gray-300">
                              ID: {timesheet.id}
                            </span>
                          </div>
                          <div className="text-sm text-gray-500">
                            {timesheet.employee?.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {format(new Date(timesheet.startDate), 'MMM dd')} - {format(new Date(timesheet.endDate), 'MMM dd, yyyy')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {timesheet.entries?.reduce((total, entry) => total + (entry.hours || 0), 0) || 0} hours
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(timesheet.status)}`}>
                        {getStatusIcon(timesheet.status)}
                        <span className="ml-1">{timesheet.status}</span>
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {format(new Date(timesheet.createdAt), 'MMM dd, yyyy')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => navigate(`/manager/timesheets/${timesheet.id}`)}
                          className="text-blue-600 hover:text-blue-900 p-1 rounded"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Reject Modal */}
      {rejectModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Reject Timesheet</h3>
            <p className="text-sm text-gray-600 mb-4">
              Please provide a reason for rejecting this timesheet.
            </p>
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Enter rejection reason..."
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              rows={3}
            />
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setRejectModal({ isOpen: false, timesheetId: null })}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={rejectMutation.isLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {rejectMutation.isLoading ? 'Rejecting...' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManagerTimesheets;
